<template>
  <div class="streaming-renderer">
    <!-- 如果正在流式传输但还没有内容，显示加载动效 -->
    <div v-if="isStreaming && !fullContent" class="loading-placeholder">
      <loading-dots />
    </div>
    <!-- 否则显示正常的消息渲染器 -->
    <unified-message-renderer
      v-else
      ref="messageRenderer"
      :message="streamingMessage"
      :streaming="isStreaming"
      :custom-config="customConfig"
      @rendered="onRendered"
    >
      <template v-slot:enhancements>
        <div v-if="isStreaming" class="streaming-indicator">
          <span class="streaming-dot"></span>
          <span class="streaming-text">正在输入...</span>
        </div>
      </template>
    </unified-message-renderer>
  </div>
</template>

<script>
import UnifiedMessageRenderer from './UnifiedMessageRenderer.vue';
import LoadingDots from '../common/LoadingDots.vue';

/**
 * 流式渲染缓冲器
 * 优化流式内容的渲染性能
 */
class StreamingBuffer {
  constructor(options = {}) {
    this.buffer = '';
    this.renderTimer = null;
    this.lastRenderTime = 0;
    
    // 配置选项
    this.chunkSize = options.chunkSize || 100;          // 每批次渲染的字符数
    this.renderInterval = options.renderInterval || 50;  // 最小渲染间隔(ms)
    this.maxBufferSize = options.maxBufferSize || 500;  // 最大缓冲区大小
    this.autoFlushDelay = options.autoFlushDelay || 100; // 自动刷新延迟(ms)
    
    // 回调函数
    this.onFlush = options.onFlush || (() => {});
    this.onAppend = options.onAppend || (() => {});
    
    // 性能统计
    this.stats = {
      totalChunks: 0,
      totalRenders: 0,
      totalChars: 0
    };
  }
  
  /**
   * 追加内容到缓冲区
   */
  append(chunk) {
    if (!chunk) return;
    
    this.buffer += chunk;
    this.stats.totalChunks++;
    this.stats.totalChars += chunk.length;
    
    this.onAppend(chunk);
    
    // 智能批处理逻辑
    const now = Date.now();
    const timeSinceLastRender = now - this.lastRenderTime;
    
    if (
      this.buffer.length >= this.maxBufferSize || // 缓冲区已满
      (this.buffer.length >= this.chunkSize && timeSinceLastRender >= this.renderInterval) // 达到批次大小且间隔足够
    ) {
      this.flush();
    } else {
      // 设置自动刷新定时器
      this.scheduleFlush(this.autoFlushDelay);
    }
  }
  
  /**
   * 计划刷新缓冲区
   */
  scheduleFlush(delay) {
    clearTimeout(this.renderTimer);
    this.renderTimer = setTimeout(() => this.flush(), delay);
  }
  
  /**
   * 立即刷新缓冲区
   */
  flush() {
    clearTimeout(this.renderTimer);
    
    if (this.buffer) {
      const content = this.buffer;
      this.buffer = '';
      this.lastRenderTime = Date.now();
      this.stats.totalRenders++;
      this.onFlush(content);
    }
  }
  
  /**
   * 强制完成并刷新所有内容
   */
  finish() {
    this.flush();
  }
  
  /**
   * 重置缓冲区
   */
  reset() {
    clearTimeout(this.renderTimer);
    this.buffer = '';
    this.lastRenderTime = 0;
    this.stats = {
      totalChunks: 0,
      totalRenders: 0,
      totalChars: 0
    };
  }
  
  /**
   * 获取统计信息
   */
  getStats() {
    return {
      ...this.stats,
      bufferSize: this.buffer.length,
      avgChunkSize: this.stats.totalChunks > 0 ? 
        Math.round(this.stats.totalChars / this.stats.totalChunks) : 0,
      renderEfficiency: this.stats.totalRenders > 0 ?
        Math.round(this.stats.totalChunks / this.stats.totalRenders * 100) / 100 : 0
    };
  }
}

export default {
  name: 'StreamingRenderer',
  
  components: {
    UnifiedMessageRenderer,
    LoadingDots
  },
  
  props: {
    // 初始消息对象
    initialMessage: {
      type: Object,
      default: () => ({})
    },
    
    // 流式配置
    streamingConfig: {
      type: Object,
      default: () => ({})
    },
    
    // 自定义渲染配置
    customConfig: {
      type: Object,
      default: () => ({})
    }
  },
  
  data() {
    return {
      // 流式消息对象
      streamingMessage: {
        ...this.initialMessage,
        content: '',
        streaming: true
      },
      
      // 流式状态
      isStreaming: false,
      
      // 完整内容
      fullContent: '',
      
      // 流式缓冲器
      streamingBuffer: null,
      
      // 性能监控
      performanceMonitor: {
        startTime: null,
        endTime: null,
        chunks: [],
        renders: []
      }
    };
  },
  
  created() {
    this.initStreamingBuffer();
  },
  
  methods: {
    /**
     * 初始化流式缓冲器
     */
    initStreamingBuffer() {
      const config = {
        chunkSize: 100,
        renderInterval: 50,
        maxBufferSize: 500,
        autoFlushDelay: 100,
        ...this.streamingConfig,
        onFlush: this.handleFlush,
        onAppend: this.handleAppend
      };
      
      this.streamingBuffer = new StreamingBuffer(config);
    },
    
    /**
     * 开始流式传输
     */
    startStreaming(initialContent = '') {
      this.isStreaming = true;
      this.fullContent = initialContent;
      this.streamingMessage = {
        ...this.initialMessage,
        content: initialContent,
        streaming: true
      };
      
      // 重置缓冲器
      this.streamingBuffer.reset();
      
      // 记录开始时间
      this.performanceMonitor.startTime = Date.now();
      this.performanceMonitor.chunks = [];
      this.performanceMonitor.renders = [];
      
      this.$emit('streaming-start');
    },
    
    /**
     * 追加流式内容
     */
    appendChunk(chunk) {
      if (!this.isStreaming) {
        console.warn('StreamingRenderer: 尝试在非流式状态下追加内容');
        return;
      }
      
      // 记录chunk信息
      this.performanceMonitor.chunks.push({
        size: chunk.length,
        time: Date.now() - this.performanceMonitor.startTime
      });
      
      // 使用缓冲器处理
      this.streamingBuffer.append(chunk);
    },
    
    /**
     * 处理缓冲区刷新
     */
    handleFlush(content) {
      this.fullContent += content;
      
      // 更新消息内容
      this.streamingMessage = {
        ...this.streamingMessage,
        content: this.fullContent
      };
      
      // 记录渲染信息
      this.performanceMonitor.renders.push({
        contentSize: this.fullContent.length,
        time: Date.now() - this.performanceMonitor.startTime
      });
      
      // 通知渲染器更新
      if (this.$refs.messageRenderer) {
        this.$refs.messageRenderer.setContent(this.fullContent);
      }
      
      this.$emit('content-update', this.fullContent);
    },
    
    /**
     * 处理内容追加
     */
    handleAppend(chunk) {
      // 可以在这里添加额外的处理逻辑
      this.$emit('chunk-append', chunk);
    },
    
    /**
     * 完成流式传输
     */
    finishStreaming() {
      if (!this.isStreaming) return;
      
      // 刷新所有缓冲内容
      this.streamingBuffer.finish();
      
      // 更新状态
      this.isStreaming = false;
      this.streamingMessage = {
        ...this.streamingMessage,
        streaming: false
      };
      
      // 通知渲染器完成
      if (this.$refs.messageRenderer) {
        this.$refs.messageRenderer.finishStreaming();
      }
      
      // 记录结束时间
      this.performanceMonitor.endTime = Date.now();
      
      // 发出完成事件
      this.$emit('streaming-finish', {
        content: this.fullContent,
        stats: this.getPerformanceStats()
      });
    },
    
    /**
     * 取消流式传输
     */
    cancelStreaming() {
      if (!this.isStreaming) return;
      
      this.streamingBuffer.reset();
      this.isStreaming = false;
      
      this.$emit('streaming-cancel');
    },
    
    /**
     * 设置完整内容（非流式）
     */
    setFullContent(content) {
      this.fullContent = content;
      this.streamingMessage = {
        ...this.initialMessage,
        content: content,
        streaming: false
      };
      
      if (this.$refs.messageRenderer) {
        this.$refs.messageRenderer.setContent(content);
      }
    },
    
    /**
     * 获取当前内容
     */
    getCurrentContent() {
      return this.fullContent;
    },
    
    /**
     * 获取性能统计
     */
    getPerformanceStats() {
      const duration = this.performanceMonitor.endTime - this.performanceMonitor.startTime;
      const chunks = this.performanceMonitor.chunks;
      const renders = this.performanceMonitor.renders;
      
      return {
        duration: duration,
        totalChunks: chunks.length,
        totalRenders: renders.length,
        totalChars: this.fullContent.length,
        avgChunkSize: chunks.length > 0 ?
          Math.round(chunks.reduce((sum, c) => sum + c.size, 0) / chunks.length) : 0,
        avgRenderInterval: renders.length > 1 ?
          Math.round(duration / (renders.length - 1)) : 0,
        charsPerSecond: duration > 0 ?
          Math.round(this.fullContent.length / (duration / 1000)) : 0,
        bufferStats: this.streamingBuffer.getStats()
      };
    },
    
    /**
     * 渲染完成回调
     */
    onRendered() {
      this.$emit('rendered');
    },
    
    /**
     * 获取渲染后的HTML
     */
    getRenderedHTML() {
      if (this.$refs.messageRenderer) {
        return this.$refs.messageRenderer.getRenderedHTML();
      }
      return '';
    }
  },
  
  beforeDestroy() {
    // 清理资源
    if (this.streamingBuffer) {
      this.streamingBuffer.reset();
    }
  }
};
</script>

<style lang="scss" scoped>
.streaming-renderer {
  position: relative;
  
  // 加载占位符
  .loading-placeholder {
    display: inline-block;
    padding: 4px 0;
  }
  
  // 流式指示器
  .streaming-indicator {
    display: inline-flex;
    align-items: center;
    margin-top: 8px;
    color: #666;
    font-size: 12px;
    
    .streaming-dot {
      display: inline-block;
      width: 8px;
      height: 8px;
      background: #0066cc;
      border-radius: 50%;
      margin-right: 6px;
      animation: streaming-pulse 1.5s ease-in-out infinite;
    }
    
    .streaming-text {
      color: #999;
    }
  }
  
  @keyframes streaming-pulse {
    0%, 100% {
      opacity: 0.3;
      transform: scale(0.8);
    }
    50% {
      opacity: 1;
      transform: scale(1.2);
    }
  }
}

// 暗色主题
@media (prefers-color-scheme: dark) {
  .streaming-renderer {
    .streaming-indicator {
      color: #aaa;
      
      .streaming-text {
        color: #666;
      }
      
      .streaming-dot {
        background: #58a6ff;
      }
    }
  }
}
</style>